import { Signal, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'

import {
  FlxValidator,
  TemplateConfig,
  boolExpression$,
  evalPropExpression$,
  expressionHasDynamicVariables,
  getProcessValue,
  getStaticAssetByKey,
  getStaticAssetByKeyTrimmed,
  localizeString,
  theme,
} from '@flowx/core-sdk'
import { ThemeComponent, getComponentIcons } from '@flowx/core-theme'

import { ExpressionLogInfo, expressionLogMeta } from '../utils/expression-log-meta'

export function getDynamicPropSignal(
  prop: string,
  templateConfig: TemplateConfig,
  skipLocalization = false,
  skipEnumLabelReplacement = false
): Signal<string> {
  return expressionHasDynamicVariables(prop)
    ? toSignal(
        getProcessValue(prop, templateConfig, {
          skipLocalization,
          keepStringValueQuotes: false,
          defaultUndefinedValue: '',
          skipEnumLabelReplacement,
        })
      )
    : signal(localizeString(prop))
}

// `logInfo` opts the evaluation into the `expression` log stream — callers say
// which kind of expression this is, identity comes from the template config.
export function getBoolExpressionSignal(
  expression: string,
  templateConfig: TemplateConfig,
  logInfo?: ExpressionLogInfo
): Signal<boolean> {
  return expression
    ? toSignal(
        boolExpression$(
          templateConfig.processInstanceUuid,
          'prototypeInstanceMeta' in templateConfig
            ? templateConfig.prototypeInstanceMeta
            : undefined,
          expression,
          expressionLogMeta(templateConfig, logInfo)
        )
      )
    : signal(false)
}

// Every caller evaluates a computed value, so the log type is the default.
export function getComputedValueSignal(
  prop: string,
  templateConfig: TemplateConfig,
  logInfo: ExpressionLogInfo = { logType: 'computed' }
): Signal<string> {
  return toSignal(
    evalPropExpression$(prop, templateConfig, expressionLogMeta(templateConfig, logInfo))
  )
}

export function getErrorMessages(
  templateConfig: TemplateConfig,
  validators: FlxValidator[] | undefined = 'validators' in templateConfig
    ? templateConfig.validators
    : undefined
): Record<string, Signal<string>> {
  return validators?.reduce(
    (messages, validator) => ({
      ...messages,
      [validator.name]: getDynamicPropSignal(validator.message, templateConfig),
    }),
    {}
  )
}

export function getContentIcon(icon: { value: string; appId: string }): string | null {
  if (!icon) {
    return null
  }
  const iconKey = (icon.appId || '') + icon.value
  return getStaticAssetByKey(iconKey)
}

export function getThemeIcon(componentType: ThemeComponent, iconName: string): string | null {
  if (!componentType || !iconName) {
    return null
  }
  return getStaticAssetByKeyTrimmed(getComponentIcons(componentType, iconName, theme()))
}
